// DRAFT — REVIEW ARTIFACT — NOT DEPLOYED
//
// The PRE-FREEZE DOCUMENT REVIEW data layer. It reads the two roll-up rows
// reporting-document-review writes into public.reporting_reconciliations for a
// cycle:
//
//   document_review_ran  — did the review actually RUN for this artifact. Without
//       it, a cycle whose review never ran is indistinguishable at
//       approve_reporting_gate leg (iii) from a clean review, because that leg
//       counts FAILING rows and zero rows is zero failures. This row is the
//       non-vacuity evidence, the same idiom as reconciliation_inputs_present.
//   document_review      — did the document contradict its own figures.
//
// Both carry blocks_gate = true, so leg (iii)
// (20260810153156_..._part2_gate_rpcs.sql:422-429) refuses the signature while
// either is failing:
//     SELECT count(*) FROM public.reporting_reconciliations
//      WHERE cycle_id = $1 AND blocks_gate IS TRUE AND pass IS NOT TRUE
//
// The findings themselves live in `detail`, not in per-finding rows: `level` has
// no CHECK, so level='narrative.exec_summary#s3' would insert, but it would
// pollute a vocabulary other consumers read AND multiply the rows leg (iii)
// counts. One roll-up row per check.
//
// NOTE the numeric columns (expected / actual / residual_pp / tolerance_pp) are
// ALWAYS NULL on these rows, by construction. The review stage judges prose
// against figures the renderer already printed; it is not a source of truth about
// any figure and is structurally incapable of publishing one into the ledger.

import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export type ReviewRuleId = "DR-1" | "DR-3" | "DR-4" | "DR-5" | "DR-6" | "DR-7";

export interface ReviewObserved {
  ref: string;
  printed: string;
  sign: "positive" | "negative" | "zero" | "not_found";
}

/** A finding from either leg. `source` discriminates: only "rules" findings can
 *  block, and an LLM finding is clamped to warn/info (V7) because a hallucinated
 *  blocker would make a hash-bound cycle unsignable for a reason nobody can
 *  refute. */
export interface ReviewFindingRow {
  id: string;
  source?: "rules" | "llm";
  rule?: ReviewRuleId;
  category?: string;
  slot: string;
  paragraph_index?: number | null;
  sentence_index?: number | null;
  quote: string;
  cell_refs?: string[];
  observed?: ReviewObserved[];
  claim_terms?: string[];
  severity: "hard_fail" | "warn" | "info";
  blocking?: boolean;
  message?: string;
  why?: string;
  confidence?: "high" | "medium" | "low";
}

export interface DocumentReviewState {
  /** true when BOTH rows exist. Absent rows mean the artifact was never reviewed. */
  present: boolean;
  /** the review completed (document_review_ran.pass). */
  ran: boolean;
  /** no blocking finding (document_review.pass). */
  clean: boolean;
  /** the SAFE-SUPERSET mirror of gate leg (iii) over these two rows only. */
  blocksGate: boolean;
  reviewerStatus: string | null;
  rulesVersion: string | null;
  artifactSha256: string | null;
  irSha256: string | null;
  verdictSha256: string | null;
  llm: Record<string, any> | null;
  findings: ReviewFindingRow[];
  mustExamine: Array<Record<string, any>>;
  counts: Record<string, any> | null;
  updatedAt: string | null;
}

const CHECK_RAN = "document_review_ran";
const CHECK_REVIEW = "document_review";

export function useDocumentReview(cycleId: string | undefined) {
  return useQuery({
    queryKey: ["reporting_document_review", cycleId],
    enabled: !!cycleId,
    staleTime: 60 * 1000,
    queryFn: async (): Promise<DocumentReviewState> => {
      const { data, error } = await supabase
        .from("reporting_reconciliations")
        .select("check_name, level, pass, blocks_gate, severity, detail, updated_at")
        .eq("cycle_id", cycleId!)
        .in("check_name", [CHECK_RAN, CHECK_REVIEW]);
      if (error) throw error;

      const rows = (data ?? []) as Array<Record<string, any>>;
      const ranRow = rows.find((r) => r.check_name === CHECK_RAN);
      const revRow = rows.find((r) => r.check_name === CHECK_REVIEW);
      const detail = (revRow?.detail ?? ranRow?.detail ?? {}) as Record<string, any>;

      // An ABSENT pair is not a pass. It means this artifact carries no review at
      // all, which is exactly the vacuity document_review_ran exists to expose —
      // so it reports as blocking here too, mirroring the server.
      const present = !!ranRow && !!revRow;
      const ran = ranRow?.pass === true;
      const clean = revRow?.pass === true;

      const blocksGate = !present ||
        (ranRow?.blocks_gate === true && ranRow?.pass !== true) ||
        (revRow?.blocks_gate === true && revRow?.pass !== true);

      return {
        present,
        ran,
        clean,
        blocksGate,
        reviewerStatus: detail.reviewer_status ?? null,
        rulesVersion: detail.rules_version ?? null,
        artifactSha256: detail.artifact_sha256 ?? null,
        irSha256: detail.ir_sha256 ?? null,
        verdictSha256: detail.verdict_sha256 ?? null,
        llm: (ranRow?.detail as any)?.llm ?? null,
        findings: Array.isArray(detail.findings) ? detail.findings : [],
        mustExamine: Array.isArray(detail.must_examine) ? detail.must_examine : [],
        counts: detail.counts ?? null,
        updatedAt: revRow?.updated_at ?? ranRow?.updated_at ?? null,
      };
    },
  });
}
